// src/components/settings/ringcentral-tab.jsx
// Owner-level "Connect RingCentral" screen — mirrors the BombBomb tab.
// One team RingCentral developer app; Client ID + Client Secret + JWT stored
// in integrations/ringCentral. The functions-api backend does all RingCentral
// calls, so credentials never reach agents' browsers. Admin-only tab.
// Never log the raw secret or JWT.
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, CheckCircle2, KeyRound, MessageSquare } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import {
  getRingCentralIntegration,
  saveRingCentralIntegration,
  saveRingCentralTeamNumber,
  fetchRcStatus,
  getRcAgentApp,
  saveRcAgentApp,
  startRcConnect,
  subscribeMyRcConnection,
  disconnectMyRc,
  subscribeUnmatchedTexts,
  dismissUnmatchedText,
  dismissUnmatchedTexts,
  linkTextNumberToRecord,
  getTextingSettings,
  saveTextingSettings,
  getAnthropicIntegration,
  RC_OAUTH_REDIRECT,
} from "../../utils/texting";
import { listenBuyerLeads } from "../../utils/firebasefunctions";
import { clientNames } from "../../utils/leadClients";
import { DEFAULT_DRIP_STEPS } from "../../utils/textJobs";
import { useActionDialog } from "../ActionDialog";

const fmtNum = (e164) => {
  const d = String(e164 || "").replace(/\D/g, "");
  return d.length === 11
    ? `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`
    : e164;
};

export default function RingCentralTab() {
  const [{ user }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [integ, setInteg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [jwt, setJwt] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [smsNumbers, setSmsNumbers] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [myConn, setMyConn] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [agentApp, setAgentApp] = useState(null);
  const [agentClientId, setAgentClientId] = useState("");
  const [agentClientSecret, setAgentClientSecret] = useState("");
  const [savingAgentApp, setSavingAgentApp] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const isAdmin = user?.role === "Admin";

  const load = async () => {
    const data = (await getRingCentralIntegration()) || {};
    setInteg(data);
    setAgentApp((await getRcAgentApp()) || {});
    setLoading(false);
    // When already connected, quietly fetch the number list so the
    // "which number do we text from" picker can render. `connected` is a
    // non-secret flag on the public doc — the secret creds are server-only.
    if (isAdmin && data.connected) {
      const res = await fetchRcStatus();
      if (res.ok) setSmsNumbers(res.smsNumbers);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live view of THIS user's personal connection (updates the moment the
  // OAuth popup finishes).
  useEffect(() => {
    if (!user?.id) return undefined;
    return subscribeMyRcConnection(user.id, setMyConn);
  }, [user?.id]);

  // Unmatched inbound texts + the lead list that powers the Link dropdown.
  const [unmatched, setUnmatched] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const [linkPick, setLinkPick] = useState({});
  useEffect(() => subscribeUnmatchedTexts(setUnmatched), []);
  useEffect(
    () =>
      listenBuyerLeads((all) =>
        setLeadOptions(
          (all || [])
            .filter((l) => l.status === "active" && !l.deletedAt)
            .map((l) => ({ id: l.id, name: clientNames(l) || l.contactName || l.id })),
        ),
      ),
    [],
  );

  // Automated-texts settings (switches ship OFF; templates prefilled).
  const [txt, setTxt] = useState(null);
  const [savingTxt, setSavingTxt] = useState(false);
  const [aiKeySaved, setAiKeySaved] = useState(false);
  useEffect(() => {
    getTextingSettings().then(setTxt);
    // Key itself is managed in the Claude AI tab; here we only show status.
    getAnthropicIntegration().then((a) => setAiKeySaved(!!a?.apiKey));
  }, []);
  const patchTxt = (k, v) => setTxt((p) => ({ ...p, [k]: v }));
  const saveTxt = async () => {
    setSavingTxt(true);
    try {
      await saveTextingSettings(
        {
          dripEnabled: !!txt.dripEnabled,
          dripSteps: (txt.dripSteps || [])
            .map((s) => ({ day: Number(s.day) || 0, template: s.template || "" }))
            .filter((s) => s.day > 0 && s.template.trim()),
          autoReplyEnabled: !!txt.autoReplyEnabled,
          portalTextEnabled: !!txt.portalTextEnabled,
          nudgeEnabled: !!txt.nudgeEnabled,
          milestoneEnabled: !!txt.milestoneEnabled,
          apptReminderEnabled: !!txt.apptReminderEnabled,
          closingReminderEnabled: !!txt.closingReminderEnabled,
          reviewRequestEnabled: !!txt.reviewRequestEnabled,
          anniversaryEnabled: !!txt.anniversaryEnabled,
          aiReplyEnabled: !!txt.aiReplyEnabled,
          pushOnInboundText: !!txt.pushOnInboundText,
          reviewLink: (txt.reviewLink || "").trim(),
          apptReminderTemplate: txt.apptReminderTemplate || "",
          closingReminderTemplate: txt.closingReminderTemplate || "",
          reviewRequestTemplate: txt.reviewRequestTemplate || "",
          anniversaryTemplate: txt.anniversaryTemplate || "",
          nudgeDays: Math.max(1, Number(txt.nudgeDays) || 7),
          milestoneStages: txt.milestoneStages || "",
          quietStartHour: Math.min(23, Math.max(0, Number(txt.quietStartHour) || 21)),
          quietEndHour: Math.min(23, Math.max(0, Number(txt.quietEndHour) || 8)),
          autoReplyTemplate: txt.autoReplyTemplate || "",
          portalLinkTemplate: txt.portalLinkTemplate || "",
          nudgeTemplate: txt.nudgeTemplate || "",
          milestoneTemplate: txt.milestoneTemplate || "",
        },
        user?.id,
      );
      toast.success("Automated-text settings saved.");
    } catch (e) {
      console.error("saveTextingSettings failed:", e);
      toast.error("Could not save — try again.");
    } finally {
      setSavingTxt(false);
    }
  };

  const linkUnmatched = async (m) => {
    const leadId = linkPick[m.id];
    const lead = leadOptions.find((l) => l.id === leadId);
    if (!lead) {
      toast.error("Pick a lead to link this number to first.");
      return;
    }
    try {
      const n = await linkTextNumberToRecord(m.fromNumber, {
        leadId: lead.id,
        contactName: lead.name,
      });
      toast.success(`Linked ${n} text${n === 1 ? "" : "s"} to ${lead.name}.`);
    } catch (e) {
      console.error("linkUnmatched failed:", e);
      toast.error("Could not link — try again.");
    }
  };

  const dismissAll = async () => {
    const count = unmatched.length;
    if (!count) return;
    const ok = await confirm({
      title: "Dismiss All Unmatched Texts",
      body: `Dismiss all ${count} unmatched text${count === 1 ? "" : "s"}?`,
      note: "They're cleared from this list, not deleted. Any that come in later still show up.",
      confirmLabel: "Dismiss All",
    });
    if (!ok) return;
    try {
      const n = await dismissUnmatchedTexts(unmatched.map((m) => m.id));
      toast.success(`Dismissed ${n} text${n === 1 ? "" : "s"}.`);
    } catch (e) {
      console.error("dismissAll failed:", e);
      toast.error("Could not dismiss them — try again.");
    }
  };

  const connectMine = async () => {
    setConnecting(true);
    try {
      const url = await startRcConnect(user.id);
      if (!url) {
        toast.error(
          "The agent connect app isn't set up yet — ask your Team Lead.",
        );
        return;
      }
      window.open(url, "_blank", "noopener");
    } catch (e) {
      console.error("startRcConnect failed:", e);
      toast.error("Could not start the connection — try again.");
    } finally {
      setConnecting(false);
    }
  };

  const disconnectMine = async () => {
    try {
      await disconnectMyRc(user.id);
      toast.success("Disconnected — your texts now use the team line.");
    } catch (e) {
      console.error("disconnectMyRc failed:", e);
      toast.error("Could not disconnect — try again.");
    }
  };

  const saveAgentApp = async () => {
    if (!agentClientId.trim() || !agentClientSecret.trim()) {
      toast.error("Both the Client ID and Client Secret are needed.");
      return;
    }
    setSavingAgentApp(true);
    try {
      await saveRcAgentApp(
        { clientId: agentClientId, clientSecret: agentClientSecret },
        user?.id,
      );
      setAgentApp((await getRcAgentApp()) || {});
      setAgentClientId("");
      setAgentClientSecret("");
      toast.success("Agent connect app saved.");
    } catch (e) {
      console.error("saveRcAgentApp failed:", e);
      toast.error("Could not save — try again.");
    } finally {
      setSavingAgentApp(false);
    }
  };

  const pickNumber = async (num) => {
    setSwitching(true);
    try {
      await saveRingCentralTeamNumber(num);
      setInteg((prev) => ({ ...(prev || {}), teamNumber: num }));
      toast.success(`Texts now send from ${num}.`);
    } catch (e) {
      console.error("Switch team number failed:", e);
      toast.error("Could not switch — please try again.");
    } finally {
      setSwitching(false);
    }
  };

  // `connected` comes from the non-secret public doc — the secret creds
  // (clientId/clientSecret/jwt) are never read into the browser.
  const connected = !!integ?.connected;

  const connect = async () => {
    if (!clientId.trim() || !clientSecret.trim() || !jwt.trim()) {
      toast.error(
        "All three values are needed — Client ID, Client Secret, and JWT.",
      );
      return;
    }
    setBusy(true);
    try {
      await saveRingCentralIntegration({ clientId, clientSecret, jwt }, user?.id);
      await load();
      setClientId("");
      setClientSecret("");
      setJwt("");
      setTestResult(null);
      toast.success("RingCentral connected — now run Test Connection.");
    } catch (e) {
      console.error("Save RingCentral credentials failed:", e);
      toast.error("Could not save — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    const res = await fetchRcStatus();
    setTesting(false);
    if (res.ok) {
      setTestResult({ ok: true, numbers: res.smsNumbers });
      toast.success(
        res.smsNumbers.length
          ? `Connected — texting from ${res.smsNumbers[0]}.`
          : "Connected, but no SMS-capable number found on this user.",
      );
    } else {
      setTestResult({ ok: false, error: res.error });
      toast.error(res.error || "Test failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-6">
        <Loader2 className="animate-spin" size={16} /> Loading…
      </div>
    );
  }

  const field = (label, value, setter, placeholder) => (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </label>
      <div className="relative">
        <KeyRound
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="password"
          value={value}
          onChange={(e) => setter(e.target.value)}
          placeholder={placeholder}
          className="w-full text-sm rounded border border-slate-300 pl-8 pr-2 py-2"
        />
      </div>
    </div>
  );

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare size={18} /> RingCentral Texting
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Text clients straight from lead and deal cards — every message logged
          on the record, replies included.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
          My RingCentral
        </label>
        {myConn?.status === "connected" ? (
          <>
            <div className="flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <CheckCircle2 size={16} /> Connected — your texts send from{" "}
              {myConn.ownNumber}
            </div>
            <button
              onClick={disconnectMine}
              className="mt-2 text-xs font-semibold border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50"
            >
              Disconnect
            </button>
          </>
        ) : myConn?.status === "reconnect-needed" ? (
          <>
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Your RingCentral connection expired (they time out after about a
              week of no texting). Reconnect to keep texting from your own
              number.
            </p>
            <button
              onClick={connectMine}
              disabled={connecting}
              className="mt-2 text-sm font-semibold text-white bg-themeColor rounded px-4 py-2 disabled:opacity-50"
            >
              {connecting ? "Opening…" : "Reconnect my RingCentral"}
            </button>
          </>
        ) : myConn?.status === "no-sms-number" ? (
          <p className="text-sm text-slate-600">
            Your RingCentral user is connected but has no text-capable number,
            so your texts use the team line. Ask your Team Lead about adding
            one in RingCentral.
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-2">
              Connect your own RingCentral once, and texts you send from the
              CRM will come from <b>your</b> number instead of the team line.
              A RingCentral login window will open — use your own RingCentral
              username and password.
            </p>
            <button
              onClick={connectMine}
              disabled={connecting}
              className="text-sm font-semibold text-white bg-themeColor rounded px-4 py-2 disabled:opacity-50"
            >
              {connecting ? "Opening…" : "Connect my RingCentral"}
            </button>
          </>
        )}
      </div>

      {unmatched.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Unmatched Texts · {unmatched.length}
            </label>
            <button
              onClick={dismissAll}
              className="text-xs font-semibold border border-slate-300 text-slate-600 rounded px-3 py-1 hover:bg-slate-50"
            >
              Dismiss All
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Incoming texts from numbers that don't match any lead. Link them to
            the right lead (the whole conversation moves over) or dismiss.
          </p>
          <div className="space-y-3">
            {unmatched.map((m) => (
              <div key={m.id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-800">
                    {fmtNum(m.fromNumber)}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    {m.createdAt
                      ? new Date(m.createdAt).toLocaleString("en-US", {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : ""}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">
                  {m.body}
                </p>
                <div className="flex gap-2 mt-2">
                  <select
                    value={linkPick[m.id] || ""}
                    onChange={(e) =>
                      setLinkPick((p) => ({ ...p, [m.id]: e.target.value }))
                    }
                    className="flex-1 text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700"
                  >
                    <option value="">Link to lead…</option>
                    {leadOptions.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => linkUnmatched(m)}
                    className="text-xs font-semibold text-white bg-themeColor rounded px-3 py-1.5"
                  >
                    Link
                  </button>
                  <button
                    onClick={() => dismissUnmatchedText(m.id)}
                    className="text-xs font-semibold border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && connected && (
        <div className="flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle2 size={16} /> Connected
          {integ?.teamNumber ? ` · texting from ${integ.teamNumber}` : ""}
          {integ?.connectedAt
            ? ` · since ${new Date(integ.connectedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
            : ""}
        </div>
      )}

      {isAdmin && connected && smsNumbers.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
            Texting Number
          </label>
          <p className="text-xs text-slate-500 mb-3">
            Every text the CRM sends comes from this number. Pick the one your
            clients should see.
          </p>
          <div className="space-y-2">
            {smsNumbers.map((num) => {
              const current = num === integ?.teamNumber;
              return (
                <div
                  key={num}
                  className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${
                    current
                      ? "border-blue-300 bg-blue-50 font-semibold text-blue-800"
                      : "border-slate-200 text-slate-700"
                  }`}
                >
                  <span>{num}</span>
                  {current ? (
                    <span className="text-xs font-bold text-linkColor">
                      ✓ Current
                    </span>
                  ) : (
                    <button
                      onClick={() => pickNumber(num)}
                      disabled={switching}
                      className="text-xs font-semibold border border-slate-300 text-slate-700 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Use this number
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isAdmin && txt && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
              Automated Texts
            </label>
            <p className="text-xs text-slate-500 mt-1">
              Each one is off until you switch it on. Placeholders like{" "}
              <code className="bg-slate-100 px-1 rounded">{"{firstName}"}</code>{" "}
              fill in automatically. Automated texts always send from the team
              line, never at night ({String(txt.quietStartHour)}:00–
              {String(txt.quietEndHour)}:00 they wait for morning), and never
              twice to the same person within 5 minutes.
            </p>
          </div>

          <div className="border border-slate-200 rounded-lg p-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                checked={!!txt.dripEnabled}
                onChange={(e) => {
                  patchTxt("dripEnabled", e.target.checked);
                  if (e.target.checked && !(txt.dripSteps || []).length) {
                    patchTxt("dripSteps", DEFAULT_DRIP_STEPS.map((s) => ({ ...s })));
                  }
                }}
              />
              New-lead drip sequence (approval cards in the Texting Center)
            </label>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Suggests a follow-up text on each day below until the lead
              replies, books an appointment, or moves forward. Nothing sends
              without approval. Tokens: {"{firstName} {agentName}"}
            </p>
            {!!txt.dripEnabled && (
              <div className="mt-2 space-y-2">
                {(txt.dripSteps || []).map((s, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <span className="text-xs text-slate-500 pt-2">Day</span>
                    <input
                      type="number"
                      min="1"
                      value={s.day}
                      onChange={(e) => {
                        const next = [...txt.dripSteps];
                        next[i] = { ...next[i], day: e.target.value };
                        patchTxt("dripSteps", next);
                      }}
                      className="w-16 text-center text-sm border border-slate-300 rounded px-1 py-1.5"
                    />
                    <textarea
                      rows={2}
                      value={s.template}
                      onChange={(e) => {
                        const next = [...txt.dripSteps];
                        next[i] = { ...next[i], template: e.target.value };
                        patchTxt("dripSteps", next);
                      }}
                      className="flex-1 text-sm border border-slate-300 rounded-md px-2 py-1.5"
                    />
                    <button
                      onClick={() =>
                        patchTxt(
                          "dripSteps",
                          txt.dripSteps.filter((_, j) => j !== i),
                        )
                      }
                      className="text-slate-400 hover:text-red-500 text-sm pt-1.5"
                      aria-label="Remove step"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  onClick={() =>
                    patchTxt("dripSteps", [
                      ...(txt.dripSteps || []),
                      { day: "", template: "" },
                    ])
                  }
                  className="text-xs font-semibold text-linkColor hover:underline"
                >
                  + Add step
                </button>
              </div>
            )}
          </div>

          {[
            {
              flag: "autoReplyEnabled",
              tplKey: "autoReplyTemplate",
              title: "New-lead auto-reply",
              hint: "Sends instantly when a lead is created by hand with a phone number (bulk imports never trigger it). Tokens: {firstName}",
            },
            {
              flag: "portalTextEnabled",
              tplKey: "portalLinkTemplate",
              title: "Portal-link text",
              hint: "Sends alongside the buyer-forms email. Tokens: {firstName} {portalLink}",
            },
            {
              flag: "nudgeEnabled",
              tplKey: "nudgeTemplate",
              title: "Follow-up nudge (approval card — never auto-sends)",
              hint: "Suggests a text on leads with no activity. Tokens: {firstName}",
            },
            {
              flag: "milestoneEnabled",
              tplKey: "milestoneTemplate",
              title: "Milestone update (approval card — never auto-sends)",
              hint: "Suggests a text when a deal reaches a stage below. Tokens: {firstName} {address} {stage}",
            },
            {
              flag: "apptReminderEnabled",
              tplKey: "apptReminderTemplate",
              title: "Appointment reminders (approval cards)",
              hint: "Day-before and morning-of suggestions for leads with an appointment date. Tokens: {firstName} {apptDate}",
            },
            {
              flag: "closingReminderEnabled",
              tplKey: "closingReminderTemplate",
              title: "Closing countdown (approval cards)",
              hint: "One week out and day-before suggestions from each deal's closing date. Tokens: {firstName} {address} {closingDate}",
            },
            {
              flag: "reviewRequestEnabled",
              tplKey: "reviewRequestTemplate",
              title: "Review request after closing (approval card)",
              hint: "Suggested 3 days after closing. Needs the Google review link below. Tokens: {firstName} {address} {reviewLink}",
            },
            {
              flag: "anniversaryEnabled",
              tplKey: "anniversaryTemplate",
              title: "Home anniversary (approval card)",
              hint: "Suggested one year after closing day. Tokens: {firstName} {address}",
            },
          ].map(({ flag, tplKey, title, hint }) => (
            <div key={flag} className="border border-slate-200 rounded-lg p-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!txt[flag]}
                  onChange={(e) => patchTxt(flag, e.target.checked)}
                />
                {title}
              </label>
              <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>
              <textarea
                rows={2}
                value={txt[tplKey] || ""}
                onChange={(e) => patchTxt(tplKey, e.target.value)}
                className="mt-2 w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
              />
            </div>
          ))}

          <div className="flex flex-wrap gap-4 items-center text-sm text-slate-700">
            <span>
              Nudge after{" "}
              <input
                type="number"
                min="1"
                value={txt.nudgeDays}
                onChange={(e) => patchTxt("nudgeDays", e.target.value)}
                className="w-14 text-center border border-slate-300 rounded px-1 py-1"
              />{" "}
              days
            </span>
            <span>
              Quiet hours{" "}
              <input
                type="number"
                min="0"
                max="23"
                value={txt.quietStartHour}
                onChange={(e) => patchTxt("quietStartHour", e.target.value)}
                className="w-14 text-center border border-slate-300 rounded px-1 py-1"
              />
              :00 to{" "}
              <input
                type="number"
                min="0"
                max="23"
                value={txt.quietEndHour}
                onChange={(e) => patchTxt("quietEndHour", e.target.value)}
                className="w-14 text-center border border-slate-300 rounded px-1 py-1"
              />
              :00
            </span>
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Milestone Stages (comma-separated)
            </label>
            <input
              type="text"
              value={txt.milestoneStages || ""}
              onChange={(e) => patchTxt("milestoneStages", e.target.value)}
              className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
              Google Review Link
            </label>
            <input
              type="text"
              value={txt.reviewLink || ""}
              onChange={(e) => patchTxt("reviewLink", e.target.value)}
              placeholder="https://g.page/r/…"
              className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                checked={!!txt.pushOnInboundText}
                onChange={(e) => patchTxt("pushOnInboundText", e.target.checked)}
              />
              Phone notification on incoming texts
            </label>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Off by default. When on, the assigned agent gets a push
              notification on their phone every time a client texts in. Leave
              off if the RingCentral app already alerts you — otherwise every
              text pings twice.
            </p>
          </div>
          <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                checked={!!txt.aiReplyEnabled}
                onChange={(e) => patchTxt("aiReplyEnabled", e.target.checked)}
              />
              ✨ AI reply drafts (approval only, never auto-sends)
            </label>
            <p className="text-[11px] text-slate-500 mt-0.5">
              When a client texts in, Claude reads the conversation and the
              lead's details and puts a drafted reply in the Texting Center.
              The ✨ button on every thread works as soon as the Claude key is
              saved. Costs pennies per draft.
            </p>
            <p className="text-[11px] text-slate-400 mt-1.5">
              {aiKeySaved
                ? "Claude key: connected ✓ (manage it in the Claude AI tab)"
                : "Add the key in Settings → Integrations → Claude AI."}
            </p>
          </div>
          <button
            onClick={saveTxt}
            disabled={savingTxt}
            className="text-sm font-semibold text-white bg-themeColor rounded px-4 py-2 disabled:opacity-50"
          >
            {savingTxt ? "Saving…" : "Save Automated Texts"}
          </button>
        </div>
      )}

      {isAdmin && (
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-xs font-semibold text-slate-400 hover:text-slate-700 text-left"
        >
          {showAdvanced
            ? "▾ Hide advanced setup"
            : "▸ Advanced setup — app credentials (already configured)"}
        </button>
      )}

      {isAdmin && showAdvanced && (
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Team Line Credentials (JWT app)
          {connected ? (
            <span className="ml-2 text-green-700 normal-case tracking-normal">
              ✓ configured
            </span>
          ) : null}
        </label>
        {field(
          connected ? "Replace Client ID" : "Client ID",
          clientId,
          setClientId,
          "Paste the app's Client ID…",
        )}
        {field(
          connected ? "Replace Client Secret" : "Client Secret",
          clientSecret,
          setClientSecret,
          "Paste the app's Client Secret…",
        )}
        {field(
          connected ? "Replace JWT" : "JWT Credential",
          jwt,
          setJwt,
          "Paste the JWT from Credentials → JWT…",
        )}
        <div className="flex gap-2">
          <button
            onClick={connect}
            disabled={busy}
            className="text-sm font-semibold text-white bg-themeColor rounded px-4 py-2 disabled:opacity-50"
          >
            {busy ? "Saving…" : connected ? "Replace Credentials" : "Connect"}
          </button>
          {connected && (
            <button
              onClick={testConnection}
              disabled={testing}
              className="text-sm font-semibold border border-slate-300 text-slate-700 rounded px-4 py-2 disabled:opacity-50"
            >
              {testing ? "Testing…" : "Test Connection"}
            </button>
          )}
        </div>
        {testResult && (
          <p
            className={`text-sm ${testResult.ok ? "text-green-700" : "text-red-600"}`}
          >
            {testResult.ok
              ? testResult.numbers.length
                ? `SMS-capable number${testResult.numbers.length === 1 ? "" : "s"}: ${testResult.numbers.join(", ")}`
                : "Connected, but no SMS-capable number on this user."
              : testResult.error}
          </p>
        )}
      </div>
      )}

      {isAdmin && showAdvanced && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">
            Agent Connect App
            {agentApp?.clientId ? (
              <span className="ml-2 text-green-700 normal-case tracking-normal">
                ✓ configured
              </span>
            ) : null}
          </label>
          <p className="text-xs text-slate-500">
            Powers the "Connect my RingCentral" button above. It's a second
            free app in the RingCentral developer portal — type "3-legged
            OAuth auth code", permissions SMS + Read Accounts, with this
            redirect URI:
          </p>
          <code className="block text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1.5 break-all text-slate-700">
            {RC_OAUTH_REDIRECT}
          </code>
          {field(
            agentApp?.clientId ? "Replace Client ID" : "Client ID",
            agentClientId,
            setAgentClientId,
            "Paste the agent app's Client ID…",
          )}
          {field(
            agentApp?.clientId ? "Replace Client Secret" : "Client Secret",
            agentClientSecret,
            setAgentClientSecret,
            "Paste the agent app's Client Secret…",
          )}
          <button
            onClick={saveAgentApp}
            disabled={savingAgentApp}
            className="text-sm font-semibold text-white bg-themeColor rounded px-4 py-2 disabled:opacity-50"
          >
            {savingAgentApp ? "Saving…" : "Save Agent App"}
          </button>
        </div>
      )}
      {dialog}
    </div>
  );
}
